import { useState, useCallback, useRef, type ReactNode, type RefObject, type ChangeEvent } from 'react'
import { getDroppedFiles } from '@uppy/utils'
import { GlobalToastType, kToastDurationLongMs } from '@/constants/constants-ui'
import { useAppDispatch } from '@/store/store-hooks'
import { openGlobalToast } from '@/store/slices/global-toast.slice'

type DriveDropzoneProps = {
  /** The content to render inside the dropzone. This is the content of the DriveView. */
  children: ReactNode
  /** Whether the dropzone is disabled. */
  disabled?: boolean
  /** Ref from `useDriveUpload` — attaches to the hidden file input. */
  fileInputRef: RefObject<HTMLInputElement>
  /** Adds files to the upload queue. */
  addFiles: (files: File[]) => void
  /** When the user picks file(s) via the OS dialog. */
  onFilesPicked: (e: ChangeEvent<HTMLInputElement>) => void
  /** Notifies parent while a file drag is over the dropzone (for upload sheet visibility). */
  onFilesBeingDragged?: (isDragging: boolean) => void
}

/**
 * Full-screen drag-and-drop overlay for file uploads.
 * Wraps DriveView content and shows an overlay when files are dragged over.
 * Similar to Google Drive's drop behavior.
 */
export function DriveDropzone({ children, disabled = false, fileInputRef, addFiles, onFilesPicked, onFilesBeingDragged }: DriveDropzoneProps) {
  const [isDragOver, setIsDragOver] = useState(false)
  const dragCounterRef = useRef(0) // Handle nested elements
  const dispatch = useAppDispatch()

  const handleInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (disabled) return
      onFilesPicked(e)
    },
    [disabled, onFilesPicked]
  )

  const handleDragEnter = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      dragCounterRef.current++

      // Only show overlay if files are being dragged and not disabled
      if (!disabled && e.dataTransfer.types.includes('Files')) {
        setIsDragOver(true)
        onFilesBeingDragged?.(true)
      }
    },
    [disabled, onFilesBeingDragged]
  )

  const handleDragLeave = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      dragCounterRef.current--

      if (dragCounterRef.current <= 0) {
        dragCounterRef.current = 0
        setIsDragOver(false)
        onFilesBeingDragged?.(false)
      }
    },
    [onFilesBeingDragged]
  )

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      const { dataTransfer } = e
      dragCounterRef.current = 0
      setIsDragOver(false)
      onFilesBeingDragged?.(false)

      if (disabled) return

      const showDropError = (error: unknown) => {
        console.error('Drop read error:', error)
        dispatch(
          openGlobalToast({
            type: GlobalToastType.ERROR,
            message: 'File could not be uploaded.',
            durationMs: kToastDurationLongMs,
          })
        )
      }

      try {
        const files = await getDroppedFiles(dataTransfer, { logDropError: showDropError })
        addFiles(files)
      } catch (error) {
        showDropError(error)
      }
    },
    [disabled, dispatch, addFiles, onFilesBeingDragged]
  )

  return (
    <div
      className="relative flex flex-col flex-1 min-h-0"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <input ref={fileInputRef} type="file" multiple disabled={disabled} className="hidden" onChange={handleInputChange} />
      {children}

      {/* Full-screen overlay when dragging */}
      {isDragOver && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-brand-500/10 border-dashed border-brand-400 rounded-t-[6px] pointer-events-none border-t-[1.5px] border-l-[1.5px] border-r-[1.5px]" />
      )}
    </div>
  )
}
